'use client';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { safeStartViewTransition } from '@//utils/viewTransition';

export enum ToastTypeEnum {
  Error = 'error',
  Success = 'success',
  Warning = 'warning',
  Info = 'info',
}

export interface Toast {
  id: string;
  type: ToastTypeEnum;
  title: string;
  description?: string;
  children?: ReactNode;
  icon?: ReactNode;
}

export type ToastBase = Omit<Toast, 'id'>;

export interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: ToastBase) => string;
  removeToast: (id: string) => void;
  updateToast: (id: string, toast: ToastBase) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: ToastBase) => {
    const id = uuidv4();

    // Use view transition API for smooth animations when adding toasts
    safeStartViewTransition(() => {
      setToasts((prev) => [...prev, { id, ...toast }]);
    });

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    // Use view transition API for smooth animations when removing toasts
    safeStartViewTransition(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    });
  }, []);

  const updateToast = useCallback((id: string, toast: ToastBase) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { id, ...toast } : t)));
  }, []);

  const value = useMemo(
    () => ({ toasts, addToast, removeToast, updateToast }),
    [toasts, addToast, removeToast, updateToast],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;
